import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import StatCards from '../components/StatCards';
import { BarChart3 } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const STATUS_COLORS = { open: '#F77F00', in_progress: '#FBBF24', resolved: '#22C55E', closed: '#6B7280' };
const CAT_COLORS = ['#F77F00', '#3B82F6', '#A855F7', '#22C55E', '#EF4444', '#FBBF24'];
const BUCKET_COLORS = ['#22C55E', '#3B82F6', '#FBBF24', '#F77F00', '#EF4444'];

export default function DashboardPage() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);

  useEffect(() => {
    api.get('/dashboard/stats').then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (loading) return <Layout><p className="text-center py-12" style={{ color: '#6B7280' }}>{t('loading', lang)}</p></Layout>;
  if (!data) return <Layout><p className="text-center py-12" style={{ color: '#EF4444' }}>Access denied</p></Layout>;

  const statusData = Object.entries(data.by_status).map(([key, value]) => ({ name: t(key, lang), value, fill: STATUS_COLORS[key] }));
  const categoryData = Object.entries(data.by_category).map(([key, value], i) => ({ name: lang === 'en' ? t(key, lang) : key, value, fill: CAT_COLORS[i % CAT_COLORS.length] }));
  const stats = { total: data.total, in_progress: data.by_status.in_progress, resolved: data.by_status.resolved, closed: data.by_status.closed };
  const bucketData = data.response_time_buckets ? Object.entries(data.response_time_buckets).map(([key, value], i) => ({ name: key, value, fill: BUCKET_COLORS[i] })) : [];
  const avgData = data.avg_response_by_status ? Object.entries(data.avg_response_by_status).filter(([, v]) => v > 0).map(([key, value]) => ({ name: t(key, lang), hours: value, fill: STATUS_COLORS[key] })) : [];

  // Common Recharts props : désactive anim sur mobile pour perf bas de gamme
  const chartAnim = !isMobile;
  const tickFont = isMobile ? 10 : 11;
  const tickFontCat = isMobile ? 9 : 10;

  // C3 Q1 option(b) — Mobile order : Workload → TopHashtags → KB count → Status → Category → Délai moyen → Tickets/semaine → Délai réponse
  // Desktop order : Status → Category → Tickets/week → Délai réponse → Délai moyen → Workload → TopHashtags → KB count (inchangé)

  const BlockWorkload = (
    <div key="workload" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{t('consultant_workload', lang)}</h3>
      <div className="space-y-2">
        {data.workload.map(w => (
          <div key={w.name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-3 rounded-lg" style={{ background: '#0F0F1A' }}>
            <span className="text-sm text-white truncate">{w.name}</span>
            <span className="text-sm font-bold whitespace-nowrap" style={{ color: '#F77F00' }}>{w.open_tickets} {t('open_tickets', lang)}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const BlockTopHashtags = (
    <div key="hashtags" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{t('top_hashtags', lang)}</h3>
      <div className="flex flex-wrap gap-2">
        {data.top_hashtags.map(h => (
          <span key={h.name} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: 'rgba(247,127,0,0.1)', color: '#F77F00' }}>
            #{h.name} <span className="opacity-60">({h.ticket_count})</span>
          </span>
        ))}
      </div>
    </div>
  );

  const BlockKBCount = (
    <div key="kb" className="apollo-card p-4 sm:p-5 flex items-center justify-between">
      <span className="text-sm truncate pr-3" style={{ color: '#9CA3AF' }}>{t('kb_articles_count', lang)}</span>
      <span className="text-2xl sm:text-3xl font-bold text-white">{data.kb_count}</span>
    </div>
  );

  const BlockStatus = (
    <div key="status" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{t('tickets_by_status', lang)}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" isAnimationActive={chartAnim}>
            {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Pie>
          <Tooltip contentStyle={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff' }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap justify-center gap-2 mt-2">
        {statusData.map(s => (
          <div key={s.name} className="flex items-center gap-1.5 text-[10px] sm:text-xs" style={{ color: '#9CA3AF' }}>
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.fill }} /> {s.name} ({s.value})
          </div>
        ))}
      </div>
    </div>
  );

  const BlockCategory = (
    <div key="category" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{t('tickets_by_category', lang)}</h3>
      <ResponsiveContainer width="100%" height={isMobile ? 260 : 250}>
        <BarChart data={categoryData} margin={{ top: 5, right: 5, bottom: isMobile ? 60 : 30, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: tickFontCat }} axisLine={false} tickLine={false} interval={0} angle={isMobile ? -30 : -15} textAnchor="end" height={isMobile ? 70 : 40} />
          <YAxis tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} width={30} />
          <Tooltip contentStyle={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff' }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={chartAnim}>
            {categoryData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const BlockAvg = avgData.length > 0 ? (
    <div key="avg" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{lang === 'fr' ? 'Délai moyen de réponse par statut (heures)' : 'Average Response Time by Status (hours)'}</h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={avgData} layout="vertical" margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis type="number" tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} width={isMobile ? 70 : 100} />
          <Tooltip contentStyle={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff' }} formatter={(value) => [value + 'h', lang === 'fr' ? 'Délai moyen' : 'Avg time']} />
          <Bar dataKey="hours" radius={[0, 6, 6, 0]} isAnimationActive={chartAnim}>
            {avgData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  ) : null;

  const BlockWeekly = (
    <div key="weekly" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{t('tickets_per_week', lang)}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data.weekly} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="week" tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} width={30} />
          <Tooltip contentStyle={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff' }} />
          <Line type="monotone" dataKey="count" stroke="#F77F00" strokeWidth={2} dot={{ fill: '#F77F00', r: 4 }} isAnimationActive={chartAnim} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  const BlockResponseTime = (
    <div key="response" className="apollo-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-white mb-3 sm:mb-4">{lang === 'fr' ? 'Délai de réponse aux tickets' : 'Ticket Response Time'}</h3>
      {bucketData.length > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={bucketData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#9CA3AF', fontSize: tickFont }} axisLine={false} tickLine={false} width={30} />
            <Tooltip contentStyle={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff' }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={chartAnim}>
              {bucketData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center h-[220px]">
          <p className="text-sm" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Pas encore de données' : 'No data yet'}</p>
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="animate-fade-in">
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <BarChart3 size={22} style={{ color: '#F77F00', flexShrink: 0 }} />
          <h1 data-testid="dashboard-title" className="text-xl sm:text-2xl font-bold text-white truncate">{t('dashboard', lang)}</h1>
        </div>

        <StatCards stats={stats} />

        {/* Mobile (< lg): workload + hashtags + kb + status + category + avg + weekly + response (actionnable first) */}
        <div className="lg:hidden space-y-4">
          {BlockWorkload}
          {BlockTopHashtags}
          {BlockKBCount}
          {BlockStatus}
          {BlockCategory}
          {BlockAvg}
          {BlockWeekly}
          {BlockResponseTime}
        </div>

        {/* Desktop (lg+): original layout preserved */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-2 gap-4 mb-4">
            {BlockStatus}
            {BlockCategory}
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {BlockWeekly}
            {BlockResponseTime}
          </div>
          {BlockAvg && <div className="mb-4">{BlockAvg}</div>}
          <div className="grid grid-cols-2 gap-4">
            {BlockWorkload}
            <div className="space-y-4">
              {BlockTopHashtags}
              {BlockKBCount}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
